/**
 * IDB and localStorage key builders for user-scoped client storage.
 *
 * Previously this module also carried a `SCHEMA_VERSION` constant + a
 * `v{N}` segment in every IDB key. That cache-bust mechanism has been
 * retired — the schemaVersion feature was a write-tax with no usable
 * benefit (see LAUNCH_PREP B1). Dropping the segment immediately
 * orphans any IDB record that was cached under the old key shape;
 * browsers refetch from the server on next bootstrap. Acceptable for
 * local-dev; we're not live yet.
 *
 * If we ever need cache-busting again, the right answer is to encode
 * a version in the DynamoDB sort key so v1 and v2 can coexist —
 * a much bigger design choice than a constant in this file.
 */

/**
 * Build an IDB key under the current user.
 *
 * Layout: `{userSub}:{...segments}` — e.g.
 *   `auth0|abc:doc:default:scratch-layout`
 *   `auth0|abc:profile:scanner:columnProfiles`
 *
 * The userSub argument is required (not pulled implicitly from a
 * module-level scope) so callers stay testable and the data flow is
 * explicit. Pass `getIdbUserScope()` from `idb-cache.ts` at the call
 * site.
 */
export function buildIdbKey(userSub: string, ...segments: string[]): string {
  if (!userSub) throw new Error('buildIdbKey: userSub is required')
  if (segments.length === 0) throw new Error('buildIdbKey: at least one segment is required')
  return [userSub, ...segments].join(':')
}

/**
 * Build a user-scoped localStorage key for state we keep in localStorage
 * (not IDB). Format: `edge:{userSub}:{...segments}`.
 *
 * Note on timing: when this is called before auth completes,
 * `getIdbUserScope()` returns `'anonymous'`. The corresponding key may
 * be read or written under that scope. After login the same call
 * returns the real userSub, so subsequent writes land under the user
 * key. Anonymous-scope orphans are deliberate accepted loss — we don't
 * migrate them.
 */
export function buildLsUserKey(userSub: string, ...segments: string[]): string {
  if (!userSub) throw new Error('buildLsUserKey: userSub is required')
  if (segments.length === 0) throw new Error('buildLsUserKey: at least one segment is required')
  return ['edge', userSub, ...segments].join(':')
}
